import { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";

const DISMISS_KEY = "csrago_install_dismissed_at";
const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // reappear after a week

export function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < COOLDOWN_MS) return;
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    if (standalone) return;

    const ios = /iphone|ipad|ipod/i.test(window.navigator.userAgent);
    if (ios) {
      setIsIOS(true);
      setVisible(true);
      return;
    }

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
    dismiss();
  };

  if (!visible) return null;

  return (
    <div
      data-testid="install-prompt"
      className="glass fixed bottom-[88px] left-3 right-3 z-[60] flex items-center gap-3 rounded-2xl border border-border/60 p-3 shadow-xl md:hidden"
    >
      <img src="/icon-mark.png" alt="CSRAGo" className="h-11 w-11 flex-shrink-0 rounded-full object-cover" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-foreground">Add CSRAGo to your home screen</p>
        {isIOS ? (
          <p className="flex items-center gap-1 text-xs text-muted-foreground">
            Tap <Share size={12} className="inline" /> then “Add to Home Screen”
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">Open it like an app — one tap, no store.</p>
        )}
      </div>
      {!isIOS && (
        <button
          onClick={install}
          data-testid="install-prompt-btn"
          className="flex flex-shrink-0 items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
        >
          <Download size={15} /> Install
        </button>
      )}
      <button
        onClick={dismiss}
        data-testid="install-prompt-dismiss"
        aria-label="Dismiss"
        className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted"
      >
        <X size={16} />
      </button>
    </div>
  );
}
